/** The Task model. Handles tracking and aggregation of time. */
export default class Task {

  /** Creates a new Task. The id defaults to the current tick. */
  constructor(text = "", id = Date.now()) {
    this.id = id;
    this.text = text;
    this.children = [];
    this.timeMap = {};
  }

  /**
   * Gives the daily breakdown of time for the past few days.
   * Includes children. Defaults to 7 days.
   */
  breakdown(tag = null, history = 7) {
    let times = [];
    let current = new Date();
    for (let i = 0; i < history; ++i) {
      let thisTime = 0;
      if (!tag || this.getTags(false).has(tag)) {
        let key = current.toDateString();
        if (Object.prototype.hasOwnProperty.call(this.timeMap, key)) {
          thisTime = this.timeMap[key];
        }
      }
      times.push(thisTime);
      current = new Date(current - 8.64e7);
    }
    times.reverse();

    // "vector" addition credit https://stackoverflow.com/a/42906936
    for (const child of this.children.values()) {
      let childBreakdown = child.breakdown(tag, history);
      times = times.map((total, index) => total + childBreakdown[index]);
    }

    return times;
  }

  /**
   * Gets the past few days in a frappe-friendly format.
   * Can bin by child or by tag. Defaults to 7 days.
   * Plug return value into `data` key within the chart.
   */
  frappe(byTag = false, filterTag = null, history = 7) {
    let data = {
      labels: [],
      datasets: [],
      update: Date.now()
    };

    if (byTag && filterTag) {
      console.warn("`filterTag` will be ignored!");
    }

    let current = new Date();
    for (let i = 0; i < history; ++i) {
      data.labels.push(current.toDateString());
      current = new Date(current - 8.64e7);
    }
    data.labels.reverse();

    if (byTag) for (const tag of this.getTags()) {
      data.datasets.push({
        name: "#" + tag,
        chartType: "bar",
        values: this.breakdown(tag, history)
      });
    } else for (const child of this.children.values()) {
      let cleanedName = child.text
        .split(" ")
        .filter(w => w[0] != "#")
        .join(" ");
      data.datasets.push({
        name: cleanedName,
        chartType: "bar",
        values: child.breakdown(filterTag, history)
      });
    }

    return data;
  }

  /** Adds time to this task to today's date by default. */
  addTime(seconds, day = new Date()) {
    let key = day.toDateString();
    if (Object.prototype.hasOwnProperty.call(this.timeMap, key)) {
      seconds += this.timeMap[key]
    }
    this.timeMap[key] = seconds;
  }

  /** Gets the total time of this task, optionally including all children. */
  time(recurse = true) {
    let t = 0;
    for (const value of Object.values(this.timeMap)) {
      t += value;
    }
    if (recurse) for (const child of this.children.values()) {
      t += child.time(recurse);
    }
    return t;
  }

  /** Finds and returns a child with the given id; null if not found. */
  getChild(id) {
    if (id === this.id) return this;
    for (const child of this.children.values()) {
      let target = child.getChild(id);
      if (target) return target;
    }
    return null;
  }

  /** Gets all tags associated with this item. Optionally searches children. */
  getTags(recurse = true) {
    let tags = new Set();
    this.text.split(" ")
      .filter(t => t[0] == "#")
      .map(t => t.substr(1))
      .filter(t => t.length != 0)
      .forEach(t => tags.add(t));
    if (recurse) for (const child of this.children.values()) {
      child.getTags(recurse).forEach(t => tags.add(t));
    }
    return tags;
  }
}
